<script setup>
import { useAlert } from 'dashboard/composables';
import { messageTimestamp } from 'shared/helpers/timeHelper';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import TableFooter from 'dashboard/components/widgets/TableFooter.vue';
import SettingsPageLayout from 'dashboard/components-next/Settings/SettingsPageLayout.vue';
import SettingsTable from 'dashboard/components-next/Settings/SettingsTable.vue';
import {
  generateTranslationPayload,
  generateLogActionKey,
} from 'dashboard/helper/auditlogHelper';
import { computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const getters = useStoreGetters();
const store = useStore();
const router = useRouter();
const records = computed(() => getters['auditlogs/getAuditLogs'].value);
const uiFlags = computed(() => getters['auditlogs/getUIFlags'].value);
const meta = computed(() => getters['auditlogs/getMeta'].value);
const agentList = computed(() => getters['agents/getAgents'].value);

const { t } = useI18n();
const route = useRoute();

const routerPage = computed(() => Number(route.query.page ?? 1));

const fetchAuditLogs = page => {
  try {
    store.dispatch('auditlogs/fetch', { page });
  } catch (error) {
    const errorMessage = error?.message || t('AUDIT_LOGS.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  }
};

const generateLogText = auditLogItem => {
  const payload = generateTranslationPayload(auditLogItem, agentList.value);
  const translationKey = generateLogActionKey(auditLogItem);

  const joinIfArray = value => {
    return Array.isArray(value) ? value.join(', ') : value;
  };

  const mergedPayload = {
    ...payload,
    attributes: joinIfArray(payload.attributes),
    values: joinIfArray(payload.values),
  };
  return t(translationKey, mergedPayload);
};

const onPageChange = page => {
  router.push({ name: 'auditlogs_list', query: { page: page } });
};

onMounted(() => {
  store.dispatch('agents/get');
  fetchAuditLogs(routerPage.value);
});

watch(routerPage, (newPage, oldPage) => {
  if (newPage !== oldPage) {
    fetchAuditLogs(newPage);
  }
});

const tableHeaders = computed(() => {
  return [
    t('AUDIT_LOGS.LIST.TABLE_HEADER.ACTIVITY'),
    t('AUDIT_LOGS.LIST.TABLE_HEADER.TIME'),
    t('AUDIT_LOGS.LIST.TABLE_HEADER.IP_ADDRESS'),
  ];
});
</script>

<template>
  <SettingsPageLayout
    :title="$t('AUDIT_LOGS.HEADER')"
    :description="$t('AUDIT_LOGS.DESCRIPTION')"
  >
    <SettingsTable
      :headers="tableHeaders"
      :is-loading="uiFlags.fetchingList"
      :is-empty="!records.length"
      :empty-message="$t('AUDIT_LOGS.LIST.404')"
    >
      <tr
        v-for="auditLogItem in records"
        :key="auditLogItem.id"
        class="hover:bg-n-solid-3 transition-colors"
      >
        <td class="px-5 py-3 whitespace-normal">
          <span class="text-sm text-n-slate-12">
            {{ generateLogText(auditLogItem) }}
          </span>
        </td>
        <td class="px-5 py-3 whitespace-nowrap">
          <span class="text-sm text-n-slate-11">
            {{
              messageTimestamp(
                auditLogItem.created_at,
                'MMM dd, yyyy hh:mm a'
              )
            }}
          </span>
        </td>
        <td class="px-5 py-3">
          <span class="text-sm text-n-slate-11">
            {{ auditLogItem.remote_address }}
          </span>
        </td>
      </tr>
    </SettingsTable>

    <TableFooter
      v-if="records.length"
      :current-page="Number(meta.currentPage)"
      :total-count="meta.totalEntries"
      :page-size="meta.perPage"
      class="border-n-weak border-t !px-0 py-4 mt-4"
      @page-change="onPageChange"
    />
  </SettingsPageLayout>
</template>
